import { useState, useEffect } from "react";
import Page from "~/components/Page";
import { useUser } from "~/context/userContext";
import { useRouter } from "next/router";
import { Paragraph03, Paragraph02, Label01 } from "~/components/Texts";
import {
  Box,
  Center,
  Button,
  useColorModeValue,
  VStack,
  HStack,
  Divider,
} from "@chakra-ui/react";
import { FiCheckCircle } from "react-icons/fi";
import Link from "next/link";
import MotionBox from "~/components/MotionBox";
import Connection from "~/components/animations/Connection";
import Warning from "~/components/animations/Warning";
import { linkMercadoPagoAccount } from "~/actions/CloudFunctions";
import Logo from "~/components/logo";
import MercadoPagoLogo from "~/components/MercadoPagoLogo";
import * as Sentry from "@sentry/nextjs";

export default function MercadoPagoAuthPage() {
  const { user, loadingUser } = useUser();
  const router = useRouter();

  const [isInitialized, setInitialized] = useState(false);
  const [isWaitingForBackendConfirmation, setWaitingForBackendConfirmation] =
    useState(false);
  const [isConfirmedByBackend, setConfirmedByBackend] = useState(false);
  const [isRejectedByBackend, setRejectedByBackend] = useState(false);

  useEffect(() => {
    // The state query parameter contains the Link Token JWT.
    // The 'access_token' query parameter contains MercadoPago OAuth access token
    // The user is necessary because the Cloud Function requires the user to be authenticated on the client.
    if (user && router.query.state && router.query.code) {
      const accessToken = router.query.code;
      const linkToken = router.query.state;

      setInitialized(true);
      setWaitingForBackendConfirmation(true);

      linkMercadoPagoAccount(linkToken, accessToken)
        .then(() => setConfirmedByBackend(true))
        .catch((error) => {
          setRejectedByBackend(true);
          Sentry.captureException(error);
        })
        .finally(() => setWaitingForBackendConfirmation(false));
    }
  }, [user, router.query.state, router.query.code]);

  return (
    <Page title="Autorizar cuenta de MercadoPago | Shop Story">
      <Box
        w="100%"
        h="100%"
        p={{ base: "2", sm: "4", md: "8" }}
        backgroundColor={useColorModeValue(
          "light.background.strong",
          "dark.background.strong"
        )}
      >
        <MotionBox
          w="100%"
          h="100%"
          animate={{
            y: [25, 0],
            opacity: [0, 1],
            transition: { duration: 0.3 },
          }}
        >
          <VStack w="100%" h="100%">
            <Center w="100%" h="100%" p={{ base: "0", md: "2" }}>
              {!isInitialized && <InitialLoadingPlaceholder />}
              {isInitialized && isRejectedByBackend && <RejectedByBackend />}
              {isInitialized && isWaitingForBackendConfirmation && (
                <WaitingForBackendConfirmation />
              )}
              {isInitialized && isConfirmedByBackend && <ConfirmedByBackend />}
            </Center>
            <HStack spacing="8">
              <Logo />
              <MercadoPagoLogo />
            </HStack>
          </VStack>
        </MotionBox>
      </Box>
    </Page>
  );
}

const ConfirmedByBackend = () => {
  return (
    <VStack
      maxW="600px"
      px="4"
      pt="8"
      pb="4"
      backgroundColor={useColorModeValue(
        "light.background.superLite",
        "dark.background.superLite"
      )}
      boxShadow="md"
      borderRadius={{ base: undefined, md: "brand" }}
      spacing="6"
    >
      <HStack color="feel.positive">
        <Label01>
          <FiCheckCircle />
        </Label01>
        <Label01>Confirmamos la operación</Label01>
      </HStack>
      <Divider />
      <Paragraph03 textAlign="center">
        La cuenta de MercadoPago fue vinculada a la tienda
      </Paragraph03>
      <Paragraph03 textAlign="center">
        A partir de este momento las ventas de tu tienda a través de MercadoPago
        serán acreditas en la nueva cuenta.
      </Paragraph03>
      <Link href="/my-shops">
        <Button colorScheme="primary" size="md">
          Volver a la tienda
        </Button>
      </Link>
    </VStack>
  );
};

const InitialLoadingPlaceholder = () => {
  return (
    <VStack spacing="4">
      <Connection />
      <Label01 textAlign="center">
        Comprobando conexión con MercadoPago...
      </Label01>
    </VStack>
  );
};

const WaitingForBackendConfirmation = () => {
  return (
    <VStack spacing="4">
      <Connection />
      <Label01 textAlign="center">
        Vinculando la tienda con MercadoPago...
      </Label01>
    </VStack>
  );
};

const RejectedByBackend = () => {
  return (
    <VStack spacing="4">
      <Warning />
      <Label01 textAlign="center">
        No pudimos conectar la tienda con MercadoPago.
      </Label01>
      <Paragraph02 textAlign="center">
        Por favor, vuelve a intentarlo desde el panel de control de tu tienda.
      </Paragraph02>
    </VStack>
  );
};
